import { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import PropTypes from 'prop-types';
import VideoCard from './VideoCard';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

/**
 * 共用影片區塊組件
 * 支持網格顯示和橫向滾動（Swiper）兩種模式
 *
 * @param {Array} videos - 影片列表
 * @param {string} title - 區塊標題
 * @param {boolean} useSwiper - 是否使用 Swiper 橫向滾動（預設 false = 網格顯示）
 * @param {Function} onVideoClick - 點擊影片的回調函數
 * @param {boolean} showViewAll - 是否顯示 VIEW ALL 按鈕
 * @param {Function} onViewAllClick - VIEW ALL 按鈕點擊事件
 */
const VideoSection = ({
    videos = [],
    title = '最新影音',
    useSwiper = false,
    onVideoClick,
    showViewAll = true,
    onViewAllClick,
}) => {
    const [activeSlideIndex, setActiveSlideIndex] = useState(0);

    if (!videos || videos.length === 0) {
        return null;
    }

    return (
        <section>
            {/* 標題列 */}
            <div className="flex items-center justify-between mb-4 md:mb-6 px-2">
                <h2 className="text-xl md:text-2xl lg:text-3xl font-medium text-[var(--color-warning)] flex items-center gap-2 md:gap-3">
                    <span className="w-1.5 h-6 md:w-2 md:h-8 bg-[var(--color-gold)] rounded-full shadow-sm"></span>
                    {title}
                </h2>
                {showViewAll && (
                    <button
                        onClick={onViewAllClick}
                        className="text-[var(--color-gold)] text-xs md:text-sm font-bold flex items-center hover:text-[var(--color-primary)] transition-colors"
                    >
                        VIEW ALL <span className="material-symbols-outlined text-sm md:text-base">play_circle</span>
                    </button>
                )}
            </div>

            {/* 影片列表 */}
            {useSwiper ? (
                <>
                    {/* 手機版：Swiper 橫向滑動（隱藏於桌面）*/}
                    <div className="md:hidden">
                        <Swiper
                            modules={[Pagination]}
                            spaceBetween={16}
                            slidesPerView="auto"
                            onSlideChange={(swiper) => setActiveSlideIndex(swiper.activeIndex)}
                            breakpoints={{
                                0: { slidesPerView: 1.2, spaceBetween: 16 },
                                640: { slidesPerView: 2.2, spaceBetween: 20 },
                            }}
                        >
                            {videos.map((video) => (
                                <SwiperSlide key={video.id} className="!w-auto">
                                    <VideoCard
                                        video={video}
                                        onVideoClick={onVideoClick}
                                        className="w-64"
                                    />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                    {/* 桌面版：三欄 Grid（隱藏於手機）*/}
                    <div className="hidden md:grid grid-cols-3 gap-6">
                        {videos.map((video) => (
                            <VideoCard
                                key={video.id}
                                video={video}
                                onVideoClick={onVideoClick}
                            />
                        ))}
                    </div>
                </>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                    {videos.map((video, index) => (
                        <VideoCard
                            key={video.id}
                            video={video}
                            onVideoClick={onVideoClick}
                            className={index >= 4 ? 'hidden md:block' : ''}
                        />
                    ))}
                </div>
            )}
        </section>
    );
};

VideoSection.propTypes = {
    videos: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            videoUrl: PropTypes.string,
            thumbnail: PropTypes.string,
            title: PropTypes.string.isRequired,
            description: PropTypes.string,
            duration: PropTypes.string,
            views: PropTypes.string,
        })
    ).isRequired,
    title: PropTypes.string,
    useSwiper: PropTypes.bool,
    onVideoClick: PropTypes.func,
    showViewAll: PropTypes.bool,
    onViewAllClick: PropTypes.func,
};

export default VideoSection;
